import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Cropper from 'v2/Cropper/Cropper';
import HelpIcon from 'v2/icons/HelpIcon';
import { getUrlFromFile } from 'v2/utils/fileUtils';
import Avatar, { AvatarProps } from '../../Avatar/Avatar';
import styles from './avatarInput.module.scss';

export interface AvatarInputProps extends Omit<AvatarProps, 'url'> {
  file: string | File;
  label?: string;
  tooltip?: string;
  onChange: (file: File | null) => void;
  readonly?: boolean;
  addBtnText?: string;
  changeBtnText?: string;
  defaultImage?: string;
  disabled?: boolean;
  removeBtnVisible?: boolean;
  removeBtnText?: string;
  isLoading?: boolean;
}

// eslint-disable-next-line complexity
const AvatarInput = ({
  className = '',
  file,
  initials,
  label,
  tooltip,
  onChange,
  readonly,
  defaultImage,
  rounded = true,
  addBtnText = 'Add',
  changeBtnText = 'Change',
  disabled = false,
  removeBtnVisible = false,
  removeBtnText = 'Remove',
  isLoading = false,
}: AvatarInputProps) => {
  const [showModal, setShowModal] = useState(false);
  const [fileUrlInternal, setFileUrlInternal] = useState<string>(
    typeof file === 'string' ? file : ''
  );
  const [croppedFile, setCroppedFile] = useState<File>();

  useEffect(() => {
    if (typeof file === 'string') {
      setFileUrlInternal(file);
    } else {
      getUrlFromFile(file).then((url) => setFileUrlInternal(url as string));
    }
  }, [file]);

  const onCropComplete = (file: File) => {
    setCroppedFile(file);
  };

  return (
    <>
      <div className={className}>
        <label className={styles.label}>
          {label}
          {tooltip ? (
            <HelpIcon className={styles.helpIcon} size={15} title={tooltip} />
          ) : null}
        </label>
        <div className={styles.fieldContainer}>
          {!isLoading ? (
            <Avatar
              className="avatar-img"
              url={fileUrlInternal || defaultImage}
              initials={initials}
              rounded={rounded}
            />
          ) : (
            <Spinner animation="border" />
          )}
          {!readonly && (
            <button
              className={classnames(
                'btn btn-primary select-file-btn',
                styles.changeButton
              )}
              type="button"
              onClick={() => setShowModal(true)}
              aria-label="changeImageBtn"
              disabled={disabled}
              data-cy="avatar-input:upload-avatar-button"
            >
              {fileUrlInternal ? changeBtnText : addBtnText}
            </button>
          )}

          {!readonly && removeBtnVisible && (
            <button
              type="button"
              className={classnames(
                'btn btn-secondary btn-sm remove-file-btn',
                styles.removePhoto
              )}
              disabled={!file || disabled}
              onClick={() => onChange(null)}
              data-cy="avatar-input:remove-avatar-button"
            >
              {removeBtnText}
            </button>
          )}
        </div>
      </div>
      {showModal && (
        <Modal
          className={className}
          show={showModal}
          onHide={() => null}
          animation={false}
        >
          <Modal.Header>
            <Modal.Title className="modalTitle">Update photo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Cropper
              fileUrl={fileUrlInternal}
              onCrop={onCropComplete}
              rounded={rounded}
            />
          </Modal.Body>
          <Modal.Footer>
            <div className={styles.footerActions}>
              <button
                className="btn btn-primary-outline"
                disabled={!croppedFile}
                onClick={() => {
                  setShowModal(false);
                  onChange(null);
                }}
              >
                Delete
              </button>
              <div>
                <button
                  className="btn btn-primary-outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!croppedFile}
                  onClick={() => {
                    setShowModal(false);
                    onChange(croppedFile as File);
                  }}
                  data-cy="avatar-input:save-avatar-ok-button"
                >
                  Ok
                </button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default AvatarInput;
